import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyersApi,
  buyerSubmissionsApi,
  type SendBuyerPrequalInvitePayload,
} from "@/api/buyers";
import { buyerKeys, buyerSubmissionKeys } from "./buyerKeys";

/**
 * The buyer's prequalification surface (PR 3b): invitations, attempts, and the
 * package letter override.
 *
 * 🔴 **Every mutation here invalidates BOTH `buyerKeys.all` and
 * `buyerSubmissionKeys.all`.** Linking, unlinking and overriding all change
 * whether a package can be generated, and `canGenerate` is read on a SUBMISSION
 * query. Invalidating only the buyer prefix leaves the submission's package
 * panel showing the old answer for the five minutes of `staleTime` with no
 * recovery — the server would accept a generate the button says is impossible,
 * or refuse one the button offers. PR 2d hit exactly this.
 *
 * Sending and resending do not touch `canGenerate`, but they share the rule
 * rather than carrying a narrower one: the cost is one extra invalidation, and
 * the failure mode of getting the distinction wrong is silent.
 */

/** Invitations sent to this buyer, newest first. */
export function useBuyerPrequalificationInvites(buyerLeadId: string, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.prequalificationInvites(buyerLeadId),
    queryFn: () => buyersApi.listPrequalificationInvites(buyerLeadId),
    enabled: enabled && Boolean(buyerLeadId),
  });
}

/** Every prequalification this buyer completed. Backs both the attempts list
 *  and the letter-override picker, which read the same key. */
export function useBuyerPrequalificationAttempts(buyerLeadId: string, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.prequalificationAttempts(buyerLeadId),
    queryFn: () => buyersApi.listPrequalificationAttempts(buyerLeadId),
    enabled: enabled && Boolean(buyerLeadId),
  });
}

function useInvalidateBuyerAndSubmissions() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: buyerKeys.all });
    void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
  };
}

/**
 * Send this buyer an invitation.
 *
 * A 409 carries the duplicate warning; the surface re-submits with
 * `confirmDuplicate: true` after the sender confirms. That is an override, never
 * a suppression — the first attempt always gets the warning.
 */
export function useSendBuyerPrequalInvite(buyerLeadId: string) {
  const invalidate = useInvalidateBuyerAndSubmissions();
  return useMutation({
    mutationFn: (payload: SendBuyerPrequalInvitePayload = {}) =>
      buyersApi.sendPrequalificationInvite(buyerLeadId, payload),
    onSuccess: invalidate,
  });
}

/**
 * Resend one invitation to its RECIPIENT — the manual nudge.
 *
 * ⚠️ Not to be confused with `PrequalInviteNudgeWorker`, which nudges the
 * SENDER about their own stalled outreach. Different action, different
 * recipient, and wiring a button labelled "nudge" to the worker's path would
 * mail the wrong person.
 *
 * One invite, one click. There is deliberately no bulk variant: the daily send
 * cap is enforced only on create, so a list-level action over this path is
 * precisely what the ceiling exists to stop.
 */
export function useResendBuyerPrequalInvite(buyerLeadId: string) {
  const invalidate = useInvalidateBuyerAndSubmissions();
  return useMutation({
    mutationFn: (inviteId: string) =>
      buyersApi.resendPrequalificationInvite(buyerLeadId, inviteId),
    onSuccess: invalidate,
  });
}

/** Link a confirmed candidate. Moves the primary pointer AND appends an
 *  attempt, so both lists change. */
export function useLinkBuyerPrequalification(buyerLeadId: string) {
  const invalidate = useInvalidateBuyerAndSubmissions();
  return useMutation({
    mutationFn: (submissionId: string) =>
      buyersApi.linkPrequalification(buyerLeadId, submissionId),
    onSuccess: invalidate,
  });
}

/** Clear the primary pointer. The attempts stand — the buyer DID complete
 *  them, and erasing that history to record a pointer correction would lose the
 *  more durable fact. */
export function useUnlinkBuyerPrequalification(buyerLeadId: string) {
  const invalidate = useInvalidateBuyerAndSubmissions();
  return useMutation({
    mutationFn: () => buyersApi.unlinkPrequalification(buyerLeadId),
    onSuccess: invalidate,
  });
}

/** Choose which letter one submission's package uses; null clears it. */
export function useSetPackageLetterOverride(submissionId: string) {
  const invalidate = useInvalidateBuyerAndSubmissions();
  return useMutation({
    mutationFn: (overrideSubmissionId: string | null) =>
      buyerSubmissionsApi.setPackageLetterOverride(submissionId, overrideSubmissionId),
    onSuccess: invalidate,
  });
}
